/**
 * Project-wide search across chapter / note / database docs (names + body).
 * `.zwdoc` bodies are searched through their plain-text projection.
 */

import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";

import { paragraphLocation, scanTextOf } from "../lib/zwdoc/scanText";

const FENCE_RE = /^(`{3,}|~{3,})(.*)$/;

export type SearchSource = "chapter" | "note" | "database";

export type SearchHitKind = "name" | "content";

export type SearchHit = {
  kind: SearchHitKind;
  source: SearchSource;
  /** Basename within that source folder (or chapter root). */
  file: string;
  /** Body text offset, or `.zwdoc` projection offset. */
  from: number;
  /** What `lineNumber` counts: legacy lines or `.zwdoc` paragraphs. */
  unit: "line" | "paragraph";
  lineNumber: number;
  snippet: string;
};

export type SearchCorpusItem = {
  source: SearchSource;
  file: string;
  /** Label shown in the UI (chapter folder title, note name, …). */
  displayName: string;
  /**
   * Document text (`.zwdoc`, or legacy Markdown) to search for content hits.
   * `nothing` = name-only (e.g. maps). Pass the live buffer for the active doc;
   * otherwise disk contents.
   */
  text: Maybe<string>;
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Peel leading YAML frontmatter so content search ignores metadata.
 * Returns the body and its start offset in the original string (so hit
 * offsets still point into the full document for scroll/jump).
 */
export function searchableDocBody(raw: string): {
  body: string;
  bodyStart: number;
} {
  let bodyStart = 0;
  let remaining = raw;
  // Up to two stacked `---` blocks (entity repair window mirrors entityBody).
  for (let i = 0; i < 2; i++) {
    if (remaining.startsWith("---\n") === false) break;
    const rest = remaining.slice(4);
    const end = rest.indexOf("\n---\n");
    if (end < 0) break;
    const consumed = 4 + end + 5;
    bodyStart += consumed;
    remaining = remaining.slice(consumed);
  }
  return { body: remaining, bodyStart };
}

function lineNumberAt(raw: string, offset: number): number {
  if (offset <= 0) return 1;
  let lines = 1;
  const end = Math.min(offset, raw.length);
  for (let i = 0; i < end; i++) {
    if (raw.charCodeAt(i) === 10) lines += 1;
  }
  return lines;
}

/**
 * Case-insensitive substring matches in the prose body (frontmatter skipped,
 * fenced code skipped). Offsets are relative to the full original text; for
 * `.zwdoc`, to its plain-text projection.
 */
export function findSearchHitsInDoc(
  raw: string,
  query: string,
): Omit<SearchHit, "source" | "file" | "kind">[] {
  const needle = query.trim();
  if (needle.length === 0) return [];

  const re = new RegExp(escapeRegExp(needle), "gi");
  const scan = scanTextOf(raw);
  if (scan.kind === "zwdoc") {
    const { projection } = scan;
    return [...projection.text.matchAll(re)].map((match) => {
      const from = match.index;
      const place = paragraphLocation(projection, from);
      return {
        from,
        unit: place.unit,
        lineNumber: place.number,
        snippet: place.snippet,
      };
    });
  }

  const { body, bodyStart } = searchableDocBody(raw);
  const hits: Omit<SearchHit, "source" | "file" | "kind">[] = [];
  let offset = 0;
  let fence: Maybe<string> = nothing;

  for (const line of body.split("\n")) {
    const fenceMatch = toMaybe(line.match(FENCE_RE));
    if (isJust(fenceMatch)) {
      const marker = (fenceMatch.value[1] ?? "").charAt(0);
      if (isNothing(fence)) fence = just(marker);
      else if (marker === fence.value) fence = nothing;
      offset += line.length + 1;
      continue;
    }

    if (isNothing(fence)) {
      re.lastIndex = 0;
      for (const match of line.matchAll(re)) {
        const from = bodyStart + offset + match.index;
        hits.push({
          from,
          unit: "line",
          lineNumber: lineNumberAt(raw, from),
          snippet: line.trim(),
        });
      }
    }

    offset += line.length + 1;
  }

  return hits;
}

export function findSearchHitsInFile(
  source: SearchSource,
  file: string,
  raw: string,
  query: string,
): SearchHit[] {
  return findSearchHitsInDoc(raw, query).map((hit) => ({
    ...hit,
    kind: "content" as const,
    source,
    file,
  }));
}

/** Case-insensitive name match against the display label and/or file basename. */
export function findNameHit(
  source: SearchSource,
  file: string,
  displayName: string,
  query: string,
): Maybe<SearchHit> {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return nothing;

  const base = file.replace(/^.*[/\\]/, "");
  const haystacks = [displayName, base];
  if (haystacks.some((h) => h.toLowerCase().includes(needle)) === false)
    return nothing;

  return just({
    kind: "name",
    source,
    file,
    from: 0,
    unit: "line",
    lineNumber: 0,
    snippet: displayName,
  });
}

/**
 * Rank hits for the search dialog. Name matches float above content; within
 * each kind, worldbuilding → writing → notes, then file name, then offset.
 */
export function rankSearchHits(hits: SearchHit[]): SearchHit[] {
  const sourceRank = (source: SearchSource): number =>
    source === "database" ? 0 : source === "chapter" ? 1 : 2;
  const kindRank = (kind: SearchHitKind): number => (kind === "name" ? 0 : 1);

  return [...hits].sort((a, b) => {
    const byKind = kindRank(a.kind) - kindRank(b.kind);
    if (byKind !== 0) return byKind;
    const bySource = sourceRank(a.source) - sourceRank(b.source);
    if (bySource !== 0) return bySource;
    const byFile = a.file.localeCompare(b.file, [], {
      sensitivity: "base",
    });
    if (byFile !== 0) return byFile;
    return a.from - b.from;
  });
}

/** Collect name + content hits for a prepared corpus. */
export function collectSearchHits(
  items: SearchCorpusItem[],
  query: string,
): SearchHit[] {
  const needle = query.trim();
  if (needle.length === 0) return [];

  const hits: SearchHit[] = [];
  for (const item of items) {
    const nameHit = findNameHit(
      item.source,
      item.file,
      item.displayName,
      needle,
    );
    if (isJust(nameHit)) hits.push(nameHit.value);

    if (isJust(item.text)) {
      hits.push(
        ...findSearchHitsInFile(
          item.source,
          item.file,
          item.text.value,
          needle,
        ),
      );
    }
  }
  return rankSearchHits(hits);
}
